import { useAppState } from 'context/AppState';
import { CropConstants } from './constants/Crop';
import lookup from 'country-code-lookup';
import { getTechnologyAlias } from 'pages/RecommendationV2/Sections/ExpertSeedAdvice/SeedCard';
import { isBrazil } from './countryCode';
import { convertYieldRangeToSacos, getYieldRangeUnit } from './yieldRangeUnit';
import { getBrazilCropSeason, getSeasonAwareCropName } from './brazilCropSeason';
import { getMaturityGroupDisplay } from './maturityGroup';

export const OsrSummary = () => {
  const {
    apiData: { recommendationCropwise, productCatalog },
  } = useAppState();

  // Early return if required data is not available
  if (!recommendationCropwise?.recommendations?.[0]) {
    console.warn('OsrSummary: Waiting for recommendationCropwise data...');
    return null;
  }

  const cropName = recommendationCropwise.recommendations[0].crop_name;
  const cropType = recommendationCropwise.recommendations[0].crop_type;
  const cropId = recommendationCropwise.recommendations[0].crop_id;
  const country = recommendationCropwise.country;
  const isDDG = recommendationCropwise.source === 'DDG';
  const isBrazilCountry = isBrazil(country);
  // Brazil sends the season only through crop_id; spell it out so the summary
  // cannot pull Safra or soy history into a Safrinha recommendation.
  const cropSeason = getBrazilCropSeason(country, cropId);
  const seasonAwareCropName = getSeasonAwareCropName(country, cropId, cropName);

  const firstProduct = recommendationCropwise.recommendations[0].products[0];
  const multiFieldConfig = recommendationCropwise.recommendations[0].multi_field.config;

  // Validate productCatalog availability
  if (!productCatalog || productCatalog.length === 0) {
    console.warn('OsrSummary: Waiting for productCatalog data...');
    return null;
  }
  const firstProductRecommendations = multiFieldConfig.recommendation.find((rec: any) =>
    rec.SelectedSeeds?.some((seed: any) => seed.SeedName === firstProduct.product_name)
  );
  const DESIRED_WEIGHTS = new Set(['BALANCED', 'VLOW', 'LOW', 'MED', 'HIGH', 'VHIGH']);
  /**
   * Retrieves product description from catalog
   * @param productName - Commercial name of the product
   * @returns Product description or null if not found
   */
  const getProductDescription = (productName: string): string | null => {
    if (!productCatalog || !productName) {
      return null;
    }
    const product = productCatalog.find((p) => p.commercialName === productName);
    if (!product || (!product.positioning && !product.sku?.[0]?.description)) {
      return null;
    }
    return product.positioning || product.sku[0].description;
  };
  const getFilteredAgronomicWeights = () => {
    return multiFieldConfig.agronomicWeights
      .filter((item) => item.Weight > 0 && !item.Name.toLowerCase().includes('yield'))
      .map((item) => ({
        Identifier: item.Identifier,
        Name: item.Name,
        Active: item.Active,
        Weight: item.Weight,
      }));
  };
  const getFilteredRankingWeights = () => {
    return multiFieldConfig.rankings.weights
      .filter((item) => DESIRED_WEIGHTS.has(item.Identifier))
      .map((item) => ({
        Name: item.Name,
        Weight: item.Weight,
        Active: item.Active,
      }));
  };
  const getTransformedTPRData = () => {
    const tprData = firstProductRecommendations?.AdditionalData?.YieldRankEurope?.tpr;
    if (!tprData) {
      return undefined;
    }
    return Object.entries(tprData).reduce((acc: any, [key, value]: [string, any]) => {
      const { Years, ...rest } = value;
      acc[key] = rest;
      return acc;
    }, {});
  };

  const convertYieldRange = (yieldRange?: string): string => {
    if (!yieldRange) {
      return '';
    }
    // Brazil stores t/ha but the UI shows sacos/ha - send what the grower sees,
    // otherwise the summary quotes t/ha numbers under a sacos/ha label.
    if (isBrazilCountry) {
      return (
        convertYieldRangeToSacos(yieldRange, {
          isDDG,
          // The UI rounds off agronomicInputs.cropName - match it exactly so the
          // summary quotes the same number the grower is looking at.
          cropName: multiFieldConfig.agronomicInputs?.cropName ?? cropName,
        }) ??
        multiFieldConfig.agronomicInputs?.userSelectedRange ??
        yieldRange
      );
    }
    if (cropName.toLowerCase() === CropConstants.SUNFLOWER_SMALLCASE) {
      const [min, max] = yieldRange.split('-').map((val) => Number.parseFloat(val.trim()));
      return `${min / 10}-${max / 10}`;
    }
    return yieldRange;
  };

  const maturityGroup = getMaturityGroupDisplay(
    isBrazilCountry,
    cropName,
    multiFieldConfig.agronomicInputs
  );

  const yieldRangeUnit = getYieldRangeUnit(
    country,
    multiFieldConfig.agronomicInputs?.extraInfo?.yieldRangeUnit ||
      multiFieldConfig.agronomicInputs?.yieldRangeUnit
  );

  const getHerbicideTraits = (productName: string) => {
    const traits = multiFieldConfig.agronomicInputs?.traits;
    if (!traits) {
      return undefined;
    }
    const technologies = productCatalog.find((p) => p.commercialName === productName)?.technology;
    const ProductTraitCategory: string[] = [];
    if (technologies) {
      const mappedTraits = traits
        .map((trait) => getTechnologyAlias(trait))
        .map((trait) => trait.trim().replaceAll(' ', ''));
      // Normalize technologies to array
      const techArray = Array.isArray(technologies) ? technologies : [technologies];

      techArray.forEach((technology) => {
        let UpdatedTechnology = technology;

        if (technology === 'Express™ / Fluence™') {
          UpdatedTechnology = 'Sulfonylurea-tolerant';
        }
        const hasTechnology = mappedTraits.includes(UpdatedTechnology);

        if (hasTechnology) {
          ProductTraitCategory.push(technology);
        }
      });
    }
    return ProductTraitCategory;
  };

  const osrSummaryJson = {
    year: recommendationCropwise.year,
    solution_type: recommendationCropwise.solution_type,
    created_on: recommendationCropwise.created_on,
    updated_on: recommendationCropwise.updated_on,
    country: recommendationCropwise.country,
    recommendations: [
      {
        crop_name: seasonAwareCropName,
        crop_type: cropType,
        crop_id: cropId,
        ...(cropSeason && { crop_season: cropSeason }),
        products: [
          {
            applied_area: firstProduct.applied_area,
            commercial_name: firstProduct.commercial_name,
            product_name: firstProduct.product_name,
            quantity_units: firstProduct.quantity_units,
            sku: {
              description: firstProduct.sku?.description,
              bagSize: firstProduct.sku?.bagSize,
            },
            quantities: firstProduct.quantities,
          },
        ],
        multi_field: {
          config: {
            session: multiFieldConfig.session,
            agronomicInputs: {
              ...(cropName !== CropConstants.SUNFLOWER_SMALLCASE && {
                // Brazil has no FAO rating - the UI hides the column there and
                // shows the maturity group instead, so send that and not fao.
                ...(isBrazilCountry
                  ? { ...(maturityGroup && { maturityGroup }) }
                  : { fao: multiFieldConfig.agronomicInputs?.fao }),
                traits: getHerbicideTraits(firstProduct.commercial_name || ''),
              }),
              cropName: seasonAwareCropName,
              ...(cropSeason && { cropSeason: cropSeason }),
              // Only correct the unit on an extraInfo that exists - do not
              // invent one. agronomicInputs.yieldRangeUnit below always carries
              // it regardless.
              extraInfo: multiFieldConfig.agronomicInputs?.extraInfo && {
                ...multiFieldConfig.agronomicInputs.extraInfo,
                yieldRangeUnit: yieldRangeUnit,
              },
              cropRanking: multiFieldConfig.agronomicInputs?.cropRanking,
              spatialClass: multiFieldConfig.agronomicInputs?.spatialClass,
              irrigated: multiFieldConfig.agronomicInputs?.irrigated,
              yieldRange: convertYieldRange(multiFieldConfig.agronomicInputs?.yieldRange),
              yieldRangeUnit: yieldRangeUnit,
              plantingDate: multiFieldConfig.agronomicInputs?.plantingDate,
              harvestingDate: multiFieldConfig.agronomicInputs?.harvestDate || '',
            },
            agronomicWeights: getFilteredAgronomicWeights(),
            rankings: {
              frequency: multiFieldConfig.rankings?.frequency,
              weights: getFilteredRankingWeights(),
            },
            recommendation: [
              {
                Acres: firstProductRecommendations?.Acres,
                AdditionalData: {
                  YieldRankEurope: {
                    country: firstProductRecommendations?.AdditionalData?.YieldRankEurope?.country,
                    texture: firstProductRecommendations?.AdditionalData?.YieldRankEurope?.texture,
                    tpr: getTransformedTPRData(),
                  },
                },
                SeedRankings: [firstProductRecommendations?.SeedRankings?.[0]],
                SeedingRate: firstProductRecommendations?.SeedingRate,
                WinningSeed: firstProductRecommendations?.WinningSeed,
                SelectedSeeds: firstProductRecommendations?.SelectedSeeds,
              },
            ],
            yieldRange: convertYieldRange(multiFieldConfig.yieldRange),
            yieldRangeUnit: yieldRangeUnit,
          },
          assignments: [
            {
              field_id:
                recommendationCropwise.recommendations[0].multi_field?.assignments?.[0]?.field_id,
              products: recommendationCropwise?.recommendations[0]?.products
                .filter((product) => product?.product_name === firstProduct?.product_name)
                .map((product) => ({
                  applied_area: product.applied_area,
                  product_id: product.product_id,
                  product_name: product.product_name,
                  total_quantity_required: product.total_quantity_required,
                  quantity_units: product.quantity_units,
                  seed_rate: recommendationCropwise?.recommendations[0]?.multi_field?.assignments
                    ?.flatMap((assignment) => assignment.products)
                    ?.find((p) => p.product_id === product.product_id)?.seed_rate
                    ? Math.round(
                      (recommendationCropwise?.recommendations[0]?.multi_field?.assignments
                        ?.flatMap((assignment) => assignment.products)
                        ?.find((p) => p.product_id === product.product_id)?.seed_rate ?? 0) / 1000
                    ) * 1000
                    : undefined,
                })),
            },
          ],
        },
      },
    ],
    source: recommendationCropwise.source,
    productDescriptions: getProductDescription(firstProduct.commercial_name || ''),
  };
  return {
    crop: cropName,
    country: recommendationCropwise.country
      ? lookup.byCountry(recommendationCropwise.country)?.iso2
      : '',
    solution_id: recommendationCropwise.id,
    solution_year: recommendationCropwise.year,
    ...(recommendationCropwise.email_id === 'ss-partner-ddg@seedselector.com' && {
      englishSummary: true,
    }),
    osr_creator: 'CSS_UI',
    recommendation_json: JSON.stringify(osrSummaryJson),
  };
};
